import { Component, type ReactNode } from 'react'
import { Button, Result, Toast } from 'antd-mobile'
import { copyToClipboard } from '@/utils/mobile'
import styles from './ErrorBoundary.module.less'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: string
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: '',
    }
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorText = `${error.toString()}\n\n${errorInfo.componentStack}`
    this.setState({
      error,
      errorInfo: errorText,
    })

    // 显示错误提示
    Toast.show({
      content: '页面发生错误',
      icon: 'fail',
    })
  }

  handleCopyError = async () => {
    const errorText = `错误信息：\n${this.state.error?.toString()}\n\n组件堆栈：\n${this.state.errorInfo}`

    const success = await copyToClipboard(errorText)
    Toast.show({
      content: success ? '错误信息已复制' : '复制失败，请手动复制',
      icon: success ? 'success' : 'fail',
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorBoundary}>
          <Result
            status="error"
            title="页面出错了"
            description="抱歉，页面遇到了一些问题"
          />

          <div className={styles.errorInfo}>
            <div className={styles.errorTitle}>错误信息：</div>
            <pre className={styles.errorText}>
              {this.state.error?.toString()}
            </pre>

            {this.state.errorInfo && (
              <>
                <div className={styles.errorTitle}>组件堆栈：</div>
                <pre className={styles.errorStack}>
                  {this.state.errorInfo}
                </pre>
              </>
            )}
          </div>

          <div className={styles.actions}>
            <Button
              block
              color="primary"
              onClick={this.handleCopyError}
              style={{ marginBottom: 12 }}
            >
              复制错误信息
            </Button>
            <Button block onClick={this.handleReload}>
              刷新页面
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
